import React, { useRef, useEffect, useState, useCallback } from "react";
import "./TimePickerWheel.css";

/**
 * iOS-style scroll wheel time picker
 * Supports mouse wheel, touch, and click-drag scrolling
 */

const WheelColumn = ({ items, selected, onSelect, formatItem }) => {
  const containerRef = useRef(null);
  const ITEM_HEIGHT = 48;
  
  // Drag state - using refs to avoid stale closures
  const dragState = useRef({
    isDragging: false,
    startY: 0,
    startScroll: 0,
    velocity: 0,
    lastY: 0,
    lastTime: 0
  });
  const animationFrame = useRef(null);

  // Scroll to selected item on mount
  useEffect(() => {
    if (containerRef.current) {
      const index = items.indexOf(selected);
      if (index !== -1) {
        containerRef.current.scrollTop = index * ITEM_HEIGHT;
      }
    }
  }, []);

  // Snap to nearest item and update selection
  const snapToNearest = useCallback(() => {
    if (!containerRef.current) return;
    
    const scrollTop = containerRef.current.scrollTop;
    const index = Math.round(scrollTop / ITEM_HEIGHT);
    const clampedIndex = Math.max(0, Math.min(index, items.length - 1));
    
    containerRef.current.scrollTo({
      top: clampedIndex * ITEM_HEIGHT,
      behavior: 'smooth'
    });
    
    onSelect(items[clampedIndex]);
  }, [items, onSelect]);

  // Mouse/touch down - start drag
  const handleDragStart = useCallback((e) => {
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    
    dragState.current = {
      isDragging: true,
      startY: clientY,
      startScroll: containerRef.current?.scrollTop || 0,
      velocity: 0,
      lastY: clientY,
      lastTime: Date.now()
    };
    
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    
    e.preventDefault();
  }, []);

  // Mouse/touch move - drag scroll
  const handleDragMove = useCallback((e) => {
    if (!dragState.current.isDragging) return;
    
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    const deltaY = dragState.current.startY - clientY;
    
    // Calculate velocity for momentum
    const now = Date.now();
    const dt = now - dragState.current.lastTime;
    if (dt > 0) {
      dragState.current.velocity = (dragState.current.lastY - clientY) / dt;
    }
    dragState.current.lastY = clientY;
    dragState.current.lastTime = now;
    
    if (containerRef.current) {
      containerRef.current.scrollTop = dragState.current.startScroll + deltaY;
    }
  }, []);

  // Mouse/touch up - end drag with momentum
  const handleDragEnd = useCallback(() => {
    if (!dragState.current.isDragging) return;
    
    const velocity = dragState.current.velocity;
    dragState.current.isDragging = false;
    
    // Apply momentum scrolling
    const momentumScroll = () => {
      if (!containerRef.current) {
        snapToNearest();
        return;
      }
      
      if (Math.abs(dragState.current.velocity) < 0.01) {
        snapToNearest();
        return;
      }
      
      containerRef.current.scrollTop += dragState.current.velocity * 16;
      dragState.current.velocity *= 0.92; // Friction
      animationFrame.current = requestAnimationFrame(momentumScroll);
    };
    
    if (Math.abs(velocity) > 0.5) {
      dragState.current.velocity = velocity;
      momentumScroll();
    } else {
      snapToNearest();
    }
  }, [snapToNearest]);

  // Handle mouse wheel scroll
  const handleWheel = useCallback(() => {
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    // Debounce snap after wheel stops
    animationFrame.current = requestAnimationFrame(() => {
      setTimeout(snapToNearest, 150);
    });
  }, [snapToNearest]);

  // Click on item to select
  const handleItemClick = useCallback((item, index) => {
    // Don't trigger click if we were dragging
    if (Math.abs(dragState.current.startY - dragState.current.lastY) > 5) return;
    
    onSelect(item);
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: index * ITEM_HEIGHT,
        behavior: 'smooth'
      });
    }
  }, [onSelect]);

  // Global event listeners for drag
  useEffect(() => {
    const onMouseMove = (e) => handleDragMove(e);
    const onMouseUp = () => handleDragEnd();
    const onTouchMove = (e) => handleDragMove(e);
    const onTouchEnd = () => handleDragEnd();
    
    // Always listen globally to catch mouseup outside the element
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
    
    // Also handle if mouse leaves the window entirely
    const onMouseLeave = (e) => {
      if (e.target === document.documentElement || e.target === document.body) {
        handleDragEnd();
      }
    };
    document.addEventListener('mouseleave', onMouseLeave);
    
    // Handle blur (user switches windows/tabs)
    const onBlur = () => handleDragEnd();
    window.addEventListener('blur', onBlur);
    
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('blur', onBlur);
      
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, [handleDragMove, handleDragEnd]);

  return (
    <div
      className="wheel-scroll"
      ref={containerRef}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onWheel={handleWheel}
    >
      <div className="wheel-spacer" />
      {items.map((item, index) => (
        <div
          key={item}
          className={`wheel-item ${selected === item ? "selected" : ""}`}
          onClick={() => handleItemClick(item, index)}
        >
          {formatItem ? formatItem(item) : item}
        </div>
      ))}
      <div className="wheel-spacer" />
    </div>
  );
};

const TimePickerWheel = ({ value, onChange }) => {
  // Parse initial value (format: "HH:mm")
  const parseTime = (timeStr) => {
    const [h, m] = timeStr ? timeStr.split(":").map(Number) : [9, 0];
    // Round minute to nearest 5
    const roundedMinute = Math.round(m / 5) * 5 % 60;
    return {
      hour: h === 0 ? 12 : h > 12 ? h - 12 : h,
      minute: roundedMinute,
      isPM: h >= 12
    };
  };

  const initial = parseTime(value);
  const [selectedHour, setSelectedHour] = useState(initial.hour);
  const [selectedMinute, setSelectedMinute] = useState(initial.minute);
  const [isPM, setIsPM] = useState(initial.isPM);
  const [isOpen, setIsOpen] = useState(false);

  const hours = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      const parsed = parseTime(value);
      setSelectedHour(parsed.hour);
      setSelectedMinute(parsed.minute);
      setIsPM(parsed.isPM);
    }
  }, [isOpen, value]);

  const handleConfirm = () => {
    // Convert back to 24hr format
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (!isPM && selectedHour === 12) {
      hour24 = 0;
    }
    
    const timeString = `${hour24.toString().padStart(2, "0")}:${selectedMinute.toString().padStart(2, "0")}`;
    onChange(timeString);
    setIsOpen(false);
  };

  const formatDisplayTime = () => {
    const parsed = parseTime(value);
    const period = parsed.isPM ? "PM" : "AM";
    return `${parsed.hour}:${parsed.minute.toString().padStart(2, "0")} ${period}`;
  };

  return (
    <div className="time-picker-wheel-container">
      {/* Display button that shows current time */}
      <button
        type="button"
        className="time-picker-display"
        onClick={() => setIsOpen(true)}
      >
        <span className="time-value">{formatDisplayTime()}</span>
        <svg className="time-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12 6 12 12 16 14"/>
        </svg>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="time-picker-overlay">
          <div className="time-picker-modal">
            <div className="time-picker-header">
              Select Time
            </div>
            
            <div className="time-picker-wheels">
              {/* Hour wheel */}
              <div className="wheel-column">
                <div className="wheel-label">Hour</div>
                <WheelColumn
                  items={hours}
                  selected={selectedHour}
                  onSelect={setSelectedHour}
                />
              </div>

              <div className="wheel-separator">:</div>

              {/* Minute wheel */}
              <div className="wheel-column">
                <div className="wheel-label">Min</div>
                <WheelColumn
                  items={minutes}
                  selected={selectedMinute}
                  onSelect={setSelectedMinute}
                  formatItem={(m) => m.toString().padStart(2, "0")}
                />
              </div>

              {/* AM/PM selector */}
              <div className="wheel-column ampm-column">
                <div className="wheel-label">Period</div>
                <div className="ampm-buttons">
                  <button
                    type="button"
                    className={`ampm-btn ${!isPM ? "active" : ""}`}
                    onClick={() => setIsPM(false)}
                  >
                    AM
                  </button>
                  <button
                    type="button"
                    className={`ampm-btn ${isPM ? "active" : ""}`}
                    onClick={() => setIsPM(true)}
                  >
                    PM
                  </button>
                </div>
              </div>

              {/* Selection highlight */}
              <div className="wheel-highlight" />
            </div>

            <div className="time-picker-actions">
              <button
                type="button"
                className="time-picker-cancel"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="time-picker-confirm"
                onClick={handleConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePickerWheel;
